import React, {useRef, useState} from 'react';
import {Box, Button, Divider, FormControl, FormHelperText, Input, Menu, MenuItem, Paper} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import ExpandMore from '@mui/icons-material/ExpandMore';
import PropTypes from 'prop-types';
import Typography from '@mui/material/Typography';
import {PLACEHOLDERS} from '../constants/ProfileSectionsKeys';

const maxChars = 1000;

const MultilineTextField = (
    {
        value = '',
        label,
        onChange,
        actions = []
    }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const theme = useTheme();
    const body2FontSize = theme.typography.body2.fontSize;
    const inputRef = useRef(null);

    const handleTextChange = (event) => {
        if (event.target.value.length <= maxChars) {
            onChange(event.target.value);
        }
    };

    const insertPlaceholder = (placeholder) => {
        const input = inputRef.current;
        if (!input) return;

        const {selectionStart, selectionEnd, value} = input;
        const newText = value.slice(0, selectionStart) + placeholder + value.slice(selectionEnd);

        if (newText.length > maxChars) {
            handleClose();
            return;
        }

        onChange(newText);
        requestAnimationFrame(() => {
            input.focus();
            input.setSelectionRange(
                selectionStart + placeholder.length,
                selectionStart + placeholder.length
            );
        });
        handleClose();
    };

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <Box>
            {
                label
                    ? <Typography
                        fullWidth
                        variant="body2"
                        fontWeight={600}
                        gutterBottom
                        sx={{textAlign: 'left'}}
                    >
                        {label}:
                    </Typography>
                    : null
            }
            <Paper
                variant="outlined"
                sx={{
                    width: '100%'
                }}
            >
                <Box
                    paddingLeft={1}
                    paddingRight={1}
                    paddingTop={0.5}
                    paddingBottom={0.5}
                    display="flex"
                    flexDirection="row"
                    gap={1}
                    height={25}
                >
                    <Button
                        onClick={handleClick}
                        variant="filled"
                        sx={{
                            fontSize: body2FontSize,
                            textTransform: 'none',
                            backgroundColor: 'rgba(0, 0, 0, 0.06)',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.12)',
                            },
                            paddingTop: 0,
                            paddingBottom: 0
                        }}
                        endIcon={<ExpandMore/>}
                    >
                        Placeholders
                    </Button>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleClose}
                    >
                        {PLACEHOLDERS.map((placeholder) => (
                            <MenuItem
                                key={placeholder.id}
                                onClick={() => insertPlaceholder(placeholder.value)}
                                sx={{fontSize: body2FontSize}}
                            >
                                {placeholder.display}
                            </MenuItem>
                        ))}
                    </Menu>
                    {
                        actions.map(({label, ...rest}, index) => (
                            <Button
                                key={`action-${index}`}
                                size="small"
                                sx={{
                                    color: '#186BBE',
                                    textTransform: 'none'
                                }}
                                {...rest}>
                                {label}
                            </Button>
                        ))
                    }
                </Box>
                <Divider/>
                <FormControl fullWidth>
                    <Input
                        inputRef={inputRef}
                        disableUnderline
                        multiline
                        rows={5}
                        value={value}
                        onChange={handleTextChange}
                        placeholder="Enter your text here..."
                        sx={{
                            padding: 1,
                        }}
                    />
                </FormControl>
            </Paper>
            <FormControl fullWidth>
                <FormHelperText
                    sx={{
                        textAlign: 'left',
                        paddingBottom: 0.5
                    }}>
                    {value.length}/{maxChars} characters
                </FormHelperText>
            </FormControl>
        </Box>
    );
};

MultilineTextField.propTypes = {
    value: PropTypes.string,
    placeholders: PropTypes.object,
    label: PropTypes.string,
    onChange: PropTypes.func,
    actions: PropTypes.array
};

export default MultilineTextField;